import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Container,
  Grid,
  Skeleton,
  Breadcrumbs,
  Link,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  TextField,
  InputAdornment,
  Fade,
} from "@mui/material";
import {
  ShoppingCartOutlined,
  FavoriteBorder,
  Favorite,
  NavigateNext,
  Search,
  FilterList,
  Clear,
  Inventory2Outlined,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import configLocal from "../../config/config.local.json";

const BASE_URL = configLocal.baseURL;

/* ─── Enterprise Brand Palette ── */
const brand = {
  navy: "#0F172A", // Darker Slate-Navy for high-end feel
  slate: "#475569", // Subdued text
  accent: "#2563EB", // Professional blue
  border: "#E2E8F0", // Soft borders
  surface: "#F8FAFC", // Light background
  white: "#FFFFFF",
  success: "#10B981",
  danger: "#EF4444",
};

/* ─── Optimized Stock Badge ── */
const StockBadge = ({ qty }) => (
  <Chip
    label={qty <= 0 ? "OUT OF STOCK" : "IN STOCK"}
    sx={{
      bgcolor: qty <= 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
      color: qty <= 0 ? brand.danger : brand.success,
      fontWeight: 700,
      fontSize: "0.65rem",
      height: 22,
      borderRadius: "4px",
      letterSpacing: "0.05em",
      border: `1px solid ${qty <= 0 ? brand.danger : brand.success}33`,
    }}
  />
);

const ProductCard = ({ product, onAddToCart, onWishlist, isWishlisted }) => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const isOutOfStock = product.quantity_in_stock <= 0;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: `1px solid ${brand.border}`,
        bgcolor: brand.white,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: brand.accent,
          boxShadow: "0 12px 24px -10px rgba(0,0,0,0.1)",
          "& .product-image": { transform: "scale(1.05)" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          pt: "100%",
          overflow: "hidden",
          bgcolor: brand.surface,
        }}
      >
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
          <StockBadge qty={product.quantity_in_stock} />
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onWishlist(product.id);
          }}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 2,
            bgcolor: brand.white,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            "&:hover": { bgcolor: brand.surface },
          }}
        >
          {isWishlisted ? (
            <Favorite sx={{ color: brand.danger, fontSize: 18 }} />
          ) : (
            <FavoriteBorder sx={{ fontSize: 18, color: brand.slate }} />
          )}
        </IconButton>

        <CardMedia
          component="img"
          className="product-image"
          image={product.photo || "https://via.placeholder.com/300"}
          onLoad={() => setImgLoaded(true)}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            p: 3,
            transition: "transform 0.5s ease",
            opacity: imgLoaded ? 1 : 0,
            filter: isOutOfStock ? "grayscale(1) opacity(0.6)" : "none",
          }}
          onClick={() => navigate(`/shop/products/${product.id}`)}
        />
        {!imgLoaded && (
          <Skeleton
            variant="rectangular"
            sx={{ position: "absolute", inset: 0 }}
          />
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: brand.accent,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          {product.category_name}
        </Typography>
        <Typography
          onClick={() => navigate(`/shop/products/${product.id}`)}
          sx={{
            fontWeight: 600,
            color: brand.navy,
            mt: 0.5,
            mb: 1.5,
            fontSize: "1rem",
            cursor: "pointer",
            height: "2.8em",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            "&:hover": { color: brand.accent },
          }}
        >
          {product.name}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{ fontWeight: 800, color: brand.navy, fontSize: "1.15rem" }}
          >
            {product.selling_price}{" "}
            <Box
              component="span"
              sx={{ fontSize: "0.7rem", color: brand.slate }}
            >
              EGP
            </Box>
          </Typography>
          {product.discount > 0 && (
            <Typography
              sx={{
                color: brand.slate,
                textDecoration: "line-through",
                fontSize: "0.85rem",
                opacity: 0.6,
              }}
            >
              {(
                (product.selling_price * 100) /
                (100 - product.discount)
              ).toFixed(0)}{" "}
              EGP
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={isOutOfStock}
          onClick={() => onAddToCart(product)}
          startIcon={<ShoppingCartOutlined />}
          sx={{
            bgcolor: brand.navy,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            py: 1,
            boxShadow: "none",
            "&:hover": { bgcolor: brand.accent, boxShadow: "none" },
          }}
        >
          {isOutOfStock ? "Restocking" : "Add to Cart"}
        </Button>
      </CardActions>
    </Card>
  );
};

const ShopCategoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const currentSearch = queryParams.get("search") || "";
  const currentSort = queryParams.get("sort") || "newest";
  const [searchInput, setSearchInput] = useState(currentSearch);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/public/shop/categories/${id}/products`,
        {
          params: {
            shop: "test.spadeplatform.com",
            search: currentSearch,
            sort: currentSort,
          },
        }
      );
      setProducts(res.data.data || []);
      // Simulate category fetch or pull from first product
      setCategory({ name: res.data.data?.[0]?.category_name || "Collection" });
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [id, currentSearch, currentSort]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box sx={{ bgcolor: brand.surface, minHeight: "100vh", pb: 10 }}>
      {/* ── Category Header ── */}
      <Box
        sx={{
          bgcolor: brand.white,
          borderBottom: `1px solid ${brand.border}`,
          pt: 4,
          pb: 4,
        }}
      >
        <Container maxWidth="xl">
          <Breadcrumbs
            separator={<NavigateNext sx={{ fontSize: 16 }} />}
            sx={{ mb: 2 }}
          >
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate("/shop")}
              sx={{ cursor: "pointer", fontSize: "0.85rem" }}
            >
              Shop
            </Link>
            <Typography
              sx={{ color: brand.navy, fontWeight: 600, fontSize: "0.85rem" }}
            >
              {category?.name}
            </Typography>
          </Breadcrumbs>

          <Grid container justifyContent="space-between" alignItems="flex-end">
            <Grid item>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: brand.navy,
                  letterSpacing: "-0.02em",
                }}
              >
                {category?.name}
              </Typography>
              <Typography sx={{ color: brand.slate, mt: 1, fontWeight: 500 }}>
                Showing {products.length} refined results
              </Typography>
            </Grid>

            <Grid item sx={{ mt: { xs: 3, md: 0 }, display: "flex", gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search collection..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate(`?search=${searchInput}`)
                }
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: brand.slate, mr: 1, fontSize: 20 }} />
                  ),
                  sx: {
                    borderRadius: "8px",
                    bgcolor: brand.surface,
                    border: "none",
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={currentSort}
                  onChange={(e) => navigate(`?sort=${e.target.value}`)}
                  IconComponent={FilterList}
                  sx={{ borderRadius: "8px", bgcolor: brand.surface }}
                >
                  <MenuItem value="newest">Newest</MenuItem>
                  <MenuItem value="price_asc">Price: Low to High</MenuItem>
                  <MenuItem value="price_desc">Price: High to Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 5 }}>
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, i) => (
              <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                <Skeleton
                  variant="rectangular"
                  height={400}
                  sx={{ borderRadius: "12px" }}
                />
              </Grid>
            ))}
          </Grid>
        ) : products.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 15 }}>
            <Inventory2Outlined
              sx={{ fontSize: 60, color: brand.border, mb: 2 }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: brand.navy }}
            >
              No products found
            </Typography>
            <Typography sx={{ color: brand.slate }}>
              Try adjusting your filters or search terms.
            </Typography>
          </Box>
        ) : (
          <Fade in timeout={500}>
            <Grid container spacing={3}>
              {products.map((p) => (
                <Grid item key={p.id} xs={12} sm={6} md={4} lg={3}>
                  <ProductCard
                    product={p}
                    onAddToCart={(p) => toast.success("Added!")}
                    onWishlist={() => {}}
                  />
                </Grid>
              ))}
            </Grid>
          </Fade>
        )}
      </Container>
      <ToastContainer position="bottom-right" />
    </Box>
  );
};

export default ShopCategoryPage;
